import { cn } from "@gymflow/helpers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { ReactNode, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { CheckIcon, CopyIcon } from "../icons";
import { ReactiveButton } from "../ReactiveButton";
import Label from "./Label";

type CopyInputProps = {
  copyInfo: string;
  className?: string;
  label?: string;
  buttons?: (onCopy: () => void) => ReactNode;
};

export const CopyInput = ({
  copyInfo,
  className,
  label,
  buttons,
}: CopyInputProps) => {
  const { t } = useTranslation();
  const { toast } = useContext(ToastContext);

  const onCopy = useCallback(() => {
    navigator.clipboard.writeText(copyInfo);

    toast({
      message: t("portal.common.copied"),
      type: "small",
    });
  }, [copyInfo, t, toast]);

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label label={label} />
      <div className="flex h-12 flex-row items-center justify-between gap-1 rounded-xl bg-gray-100 pl-3 pr-1">
        <div className="w-full truncate text-base font-medium text-gray-950">
          {copyInfo}
        </div>
        {buttons?.(onCopy) ?? (
          <ReactiveButton
            size="small"
            className="aspect-square p-0"
            onClick={() => onCopy()}
            reactiveContent={<CheckIcon pathClassName="stroke-gray-950" />}
          >
            <CopyIcon className="size-5" pathClassName="stroke-gray-950" />
          </ReactiveButton>
        )}
      </div>
    </div>
  );
};
